import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  ArrowUpRight,
  ArrowDownLeft,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  MessageCircle,
  Check,
  X,
  Search,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import EmptyState from '../../components/EmptyState';
import { EmptyExpenses } from '../../components/illustrations';
import StatusBadge from '../../components/ui/status-badge';
import NeoPayProofLink from '../../components/NeoPayProofLink';
import NeoPayBadge from '../../components/NeoPayBadge';
import TripImportSheet from '../../components/TripImportSheet';
import { FileSpreadsheet } from 'lucide-react';
import { categoryIcons, categoryColors } from './categoryMaps';

export default function ExpensesTab({ ctx }) {
  const {
    group,
    user,
    isAdmin,
    formatCurrency,
    setShowAddExpense,
    setShowBulkAdd,
    setTransferMode,
    setShowTransferModal,
    openEditExpense,
    handleReverseExpense,
    handleResubmitExpense,
    handleOpenComments,
    fetchGroup,
  } = ctx;

  // iter168 — inline payer-consent action (mirrors the Approvals page).
  // Shown right on the expense card when the current user is the named payer
  // and consent is still pending. Closes the "who knew?" gap for members who
  // don't routinely visit the Approvals bell.
  const [consentBusy, setConsentBusy] = useState(null); // expense id
  // iter200 — trip excel import sheet
  const [showImportSheet, setShowImportSheet] = useState(false);
  // iter238 — quick search + month filter so old entries are easy to find
  const [search, setSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState('all');

  const monthKey = (e) => (e.date || e.created_at || '').slice(0, 7);
  const months = useMemo(() => {
    const seen = new Map();
    for (const e of group?.expenses || []) {
      const k = monthKey(e);
      if (k && !seen.has(k)) {
        const [y, m] = k.split('-');
        seen.set(k, new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' }));
      }
    }
    return [...seen.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [group?.expenses]);

  const filteredExpenses = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (group?.expenses || []).filter((e) => {
      if (monthFilter !== 'all' && monthKey(e) !== monthFilter) return false;
      if (!q) return true;
      return (
        (e.description || '').toLowerCase().includes(q) ||
        (e.paid_by_name || '').toLowerCase().includes(q) ||
        (e.category || '').toLowerCase().includes(q) ||
        String(Math.round(e.amount || 0)).includes(q)
      );
    });
  }, [group?.expenses, search, monthFilter]);

  const handleInlineConsent = async (expenseId, decision) => {
    if (consentBusy) return;
    setConsentBusy(expenseId);
    try {
      await api.post(`/expenses/${expenseId}/payer-consent`, { decision });
      toast.success(decision === 'approved'
        ? 'Confirmed — admin can now approve.'
        : 'Marked as not yours. Expense auto-rejected.');
      if (fetchGroup) await fetchGroup();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update consent');
    } finally {
      setConsentBusy(null);
    }
  };

  return (
    <div data-testid="expenses-tab-body" className="space-y-3">
      <div className="grid grid-cols-3 gap-2 mb-2">
        <Button
          onClick={() => setShowAddExpense(true)}
          data-testid="add-expense-btn"
          className="rounded-full h-10 col-span-1 text-xs sm:text-sm font-bold"
        >
          <Plus className="w-4 h-4 mr-1" />
          <span className="truncate">Add Expense</span>
        </Button>
        <Button
          onClick={() => setShowBulkAdd && setShowBulkAdd(true)}
          data-testid="bulk-add-btn"
          variant="outline"
          className="rounded-full h-10 col-span-1 border-2 text-xs sm:text-sm font-bold"
        >
          <Plus className="w-4 h-4 mr-1" />
          <span className="truncate">Bulk Add</span>
        </Button>
        <Button
          onClick={() => { setTransferMode('send'); setShowTransferModal(true); }}
          data-testid="send-coins-btn"
          variant="outline"
          className="rounded-full h-10 col-span-1 border-2 text-xs sm:text-sm font-bold"
        >
          <ArrowUpRight className="w-4 h-4 mr-1" />
          <span className="truncate">Send</span>
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-2 mb-2 sm:hidden">
        <Button
          onClick={() => { setTransferMode('request_peer'); setShowTransferModal(true); }}
          data-testid="request-coins-btn"
          variant="outline"
          className="rounded-full h-10 border-2 text-xs sm:text-sm font-bold"
        >
          <ArrowDownLeft className="w-4 h-4 mr-1" />
          <span className="truncate">Request</span>
        </Button>
      </div>

      {/* iter312 — Excel expense import: trip admins, or super-admins in any group */}
      {((group?.group_type === 'trip' && isAdmin) || user?.is_super_admin) && (
        <button
          type="button"
          onClick={() => setShowImportSheet(true)}
          data-testid="trip-import-btn"
          className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-full border-2 border-dashed border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors text-xs font-semibold mb-2"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" />
          Import expenses from Excel
        </button>
      )}

      <TripImportSheet
        open={showImportSheet}
        onOpenChange={setShowImportSheet}
        groupId={group?.id}
        groupName={group?.name}
        onImported={() => fetchGroup?.()}
      />

      {group?.expenses?.length === 0 ? (
        <EmptyState
          illustration={EmptyExpenses}
          title="No expenses yet"
          description="Add your first expense to start splitting with the group."
          testId="no-expenses"
          cta={
            <Button onClick={() => setShowAddExpense(true)} className="rounded-full press-scale" data-testid="empty-add-expense-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          }
        />
      ) : (
        <>
          {/* iter238 — search + month filter */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search description, payer, amount…"
                data-testid="expense-search-input"
                className="pl-9 h-10 border-2 rounded-full text-sm"
              />
            </div>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              data-testid="expense-month-filter"
              className="h-10 px-3 border-2 rounded-full bg-background text-sm font-semibold max-w-[130px]"
            >
              <option value="all">All months</option>
              {months.map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </select>
          </div>
          {(search || monthFilter !== 'all') && (
            <p className="text-xs text-muted-foreground px-1" data-testid="expense-filter-count">
              {filteredExpenses.length} of {group.expenses.length} expenses
              {filteredExpenses.length !== group.expenses.length && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setMonthFilter('all'); }}
                  data-testid="expense-filter-clear"
                  className="ml-2 font-bold text-primary hover:underline"
                >
                  Clear
                </button>
              )}
            </p>
          )}
          {filteredExpenses.length === 0 && (
            <Card className="p-6 border-2 rounded-2xl text-center text-sm text-muted-foreground" data-testid="expense-no-matches">
              No expenses match your filter.
            </Card>
          )}
        <AnimatePresence>
          {filteredExpenses.map((expense, index) => {
            const Icon = categoryIcons[expense.category] || MoreHorizontal;

            return (
              <motion.div
                key={expense.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10, transition: { duration: 0.1, delay: 0 } }}
                transition={{ duration: 0.2, delay: Math.min(index, 12) * 0.03 }}
              >
                <Card
                  className="p-4 border-2 rounded-2xl"
                  data-testid={`expense-item-${expense.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${categoryColors[expense.category] || categoryColors.miscellaneous}`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold truncate">{expense.description}</h4>
                        <StatusBadge expense={expense} />
                        {/* iter198 — NeoPay authenticity pill (shows ledger ✓ when mirrored) */}
                        {expense.status === 'approved' && (
                          <NeoPayBadge id={expense.id} type="expense" isAdmin={isAdmin} />
                        )}
                        {/* iter131 — proof-on-chain link */}
                        <NeoPayProofLink
                          url={expense.neopay_proof_url}
                          testId={`neopay-proof-expense-${expense.id}`}
                          label="View this expense on NeoPay ledger"
                          size="xs"
                        />
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Paid by {expense.paid_by_name}{expense.paid_by !== 'group_fund' ? ` • Split: ${expense.split_among?.length} people` : ''}
                      </p>
                      {/* iter167 (Q3c) — Created/Approved audit trail */}
                      <p className="text-xs text-muted-foreground/80 mt-0.5" data-testid={`expense-audit-${expense.id}`}>
                        {expense.created_at && (
                          <><span className="font-medium">{new Date(expense.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span> · </>
                        )}
                        Added by <span className="font-medium">{expense.created_by_name || 'Unknown'}</span>
                        {expense.status === 'approved' && expense.approved_by_name && (
                          <> · Approved by <span className="font-medium">{expense.approved_by_name}</span></>
                        )}
                        {expense.payer_consent_required && expense.payer_consent_status === 'pending' && (
                          <> · <span className="text-amber-600 font-semibold">Waiting for {expense.paid_by_name} to confirm</span></>
                        )}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="font-display text-lg font-bold tabular-nums tracking-tight amount-display">
                          {/* iter191 — dual-currency display: when the expense was
                              logged in the trip currency, show that as the primary
                              number (it's what the user typed/paid) and the converted
                              home-currency below in a smaller line. */}
                          {expense.entry_currency && expense.entry_currency !== (group?.currency || 'INR')
                            ? `${(({USD:'$',EUR:'€',GBP:'£',AED:'د.إ',SGD:'S$',AUD:'A$',CAD:'C$',JPY:'¥'})[expense.entry_currency] || expense.entry_currency)}${Math.round(expense.entry_amount).toLocaleString('en-IN')}`
                            : formatCurrency(expense.amount)}
                        </p>
                        {expense.entry_currency && expense.entry_currency !== (group?.currency || 'INR') && (
                          <p className="text-xs text-muted-foreground tabular-nums" data-testid={`expense-home-amount-${expense.id}`}>
                            {formatCurrency(expense.amount)}
                          </p>
                        )}
                      </div>
                      {expense.status === 'pending' && expense.created_by === user?.id && (
                        <button
                          onClick={() => openEditExpense(expense)}
                          className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors"
                          data-testid={`edit-expense-btn-${expense.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {expense.status === 'approved' && !expense.reversed && isAdmin && (
                        <button
                          onClick={() => handleReverseExpense(expense.id)}
                          className="w-8 h-8 rounded-lg bg-red-500/10 text-red-500 flex items-center justify-center hover:bg-red-500/20 transition-colors"
                          data-testid={`reverse-expense-btn-${expense.id}`}
                          title="Reverse this expense"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      {expense.status === 'rejected' && expense.created_by === user?.id && (
                        <button
                          onClick={() => handleResubmitExpense(expense.id)}
                          className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center hover:bg-blue-500/20 transition-colors"
                          data-testid={`resubmit-expense-btn-${expense.id}`}
                          title="Resubmit for approval"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* iter168 — Inline payer-consent prompt: shown on the
                       group's own expense card when the current user is the
                       named payer and consent is still pending. Lets them
                       confirm/reject without going to Approvals. */}
                  {expense.payer_consent_required
                    && expense.payer_consent_status === 'pending'
                    && expense.paid_by === user?.id && (
                    <div
                      className="mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/40 flex items-start sm:items-center gap-3 flex-col sm:flex-row"
                      data-testid={`inline-payer-consent-${expense.id}`}
                    >
                      <div className="flex-1 min-w-0 text-xs leading-snug">
                        <p className="font-bold mb-0.5">Did you pay this?</p>
                        <p className="text-muted-foreground">
                          {expense.created_by_name || 'Someone'} added this saying you paid. Confirm to unlock admin approval.
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0 w-full sm:w-auto">
                        <Button
                          onClick={() => handleInlineConsent(expense.id, 'approved')}
                          disabled={consentBusy === expense.id}
                          data-testid={`inline-payer-consent-yes-${expense.id}`}
                          className="flex-1 sm:flex-none rounded-full h-9 text-xs bg-positive hover:bg-positive/90"
                        >
                          <Check className="w-3.5 h-3.5 mr-1" /> Yes, I paid
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleInlineConsent(expense.id, 'rejected')}
                          disabled={consentBusy === expense.id}
                          data-testid={`inline-payer-consent-no-${expense.id}`}
                          className="flex-1 sm:flex-none rounded-full h-9 text-xs border-2 border-destructive text-destructive hover:bg-destructive hover:text-white"
                        >
                          <X className="w-3.5 h-3.5 mr-1" /> No
                        </Button>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => handleOpenComments(expense.id)}
                    className="flex items-center gap-1.5 mt-2 ml-13 text-xs text-muted-foreground hover:text-primary transition-colors"
                    data-testid={`comment-btn-${expense.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Comments
                  </button>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>
        </>
      )}
    </div>
  );
}
